import { Button } from "@component/button";
import { ArrowRightIcon, AlertCircleIcon } from "lucide-react";
import RejectionIcon from "@component/rejection-icon";

interface Props {
    isLoading?: boolean;
    title?: string;
    message?: string;
    skills?: string[];
    recommendations?: string[];
    continueUrl: () => void;
    continueText?: string;
    variant?: "resume" | "assessment" | "interview" | "interview-short" | string;
    state?: "rejection" | "error";
    className?: string;
}

export default function ApplyRejectionCard({
    isLoading = false,
    title = "",
    message = "",
    continueUrl,
    continueText = "Continue",
    variant = "resume",
    state = "rejection",
    className = "",
}: Props) {
    // All variants use yellow styling for rejection and red for error
    const containerStyle = "bg-white dark:bg-gray-900";

    // Display appropriate title based on variant and state
    const getTitle = () => {
        if (state === "error") {
            switch (variant) {
                case "resume":
                    return "Resume Evaluation Error";
                case "assessment":
                    return "Evaluation Error";
                case "interview":
                    return "AI Interview Evaluation Error";
                default:
                    return title;
            }
        } else {
            return "Application Unsuccessful"
        }
    };

    // Get message based on variant and state
    const getMessage = () => {
        if (state === "error") {
            switch (variant) {
                case "resume":
                    return "Sorry, we encountered an error while evaluating your resume.";
                case "assessment":
                    return "Sorry, we encountered an error while evaluating your results.";
                case "interview":
                    return "Sorry, we encountered an error while evaluating your AI interview responses.";
                default:
                    return message;
            }
        } else {
            switch (variant) {
                case "resume":
                    return "After reviewing your resume, we found that it does not meet the criteria for this role.";
                case "assessment":
                    return "Thank you for completing the form. \n\nUnfortunately, your results did not meet the criteria required for this role.";
                case "interview":
                    return "Thank you for completing the AI interview. \n\nUnfortunately, your responses did not meet the criteria required for this role.";
                default:
                    return message;
            }
        }
    };

    return (
        <div
            className={`${containerStyle} rounded-3xl p-8 shadow-2xl bg-white text-center w-full mx-auto ${className}`}
            style={{
                boxShadow: '0px 0px 0px #000000, 0px 4px 30px -4px rgba(0, 0, 0, 0.3)'
            }}
        >
            {state === "error" ? (
                <div className="w-20 h-20 bg-red-100 dark:bg-red-900/30 rounded-full flex items-center justify-center mx-auto mb-6">
                    <AlertCircleIcon className="h-10 w-10 text-red-600 dark:text-red-400" />
                </div>
            ) : (
                <RejectionIcon
                    className={`mx-auto mb-6`}
                    variant={variant}
                />
            )}

            <h1 className="text-3xl font-bold mb-2">{getTitle()}</h1>
            <p className="text-gray-600 dark:text-gray-400 mb-8 max-w-md mx-auto whitespace-pre-line">
                {getMessage()}
            </p>
            <Button
                variant="default"
                className={`w-full sm:w-auto bg-blue-600`}
                onClick={continueUrl}
                disabled={isLoading}
            >
                {continueText}
                <ArrowRightIcon size={16} />
            </Button>
        </div>
    );
}
